import { useCallback, useEffect, useState } from "react";
import { DocumentNode } from "apollo-boost";
import { Event, EventConnection, PageInfo } from "../generated/lmsTypes";
import {
  COURSE_BOOKABLE_EVENTS,
  COURSE_NON_BOOKABLE_EVENTS,
} from "../queries/learningPaths";
import { useLmsClient } from "./lms";

const PAGE_SIZE = 100;

// Safety valve: caps a pathological course at 20 requests per query
// (pages of 100).
export const MAX_COURSE_EVENTS = 2000;

/**
 * Fetches every bookable and non-bookable event for a course by walking all
 * pages of both queries, so callers see the complete list rather than the
 * server's default page.
 */
export const useAllCourseEvents = (courseCode: string) => {
  const client = useLmsClient();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const [bookableEvents, setBookableEvents] = useState<Event[]>([]);
  const [nonBookableEvents, setNonBookableEvents] = useState<Event[]>([]);

  const fetchAllEvents = useCallback(
    async (query: DocumentNode) => {
      const events: Event[] = [];
      let pageInfo: PageInfo | null = null;
      while (
        (!pageInfo || pageInfo.hasNextPage) &&
        events.length < MAX_COURSE_EVENTS
      ) {
        const response: { data: { events: EventConnection } } =
          await client.query({
            query,
            variables: {
              courseCode,
              first: PAGE_SIZE,
              after: pageInfo ? pageInfo.endCursor : undefined,
            },
          });
        const edges = response.data.events.edges ?? [];
        if (edges.length === 0) {
          break;
        }
        events.push(
          ...edges.flatMap((edge) => (edge?.node ? [edge.node] : [])),
        );
        pageInfo = response.data.events.pageInfo ?? null;
        if (!pageInfo) {
          break;
        }
        // Without a cursor we would ask for the same page again, so a server
        // that promises another page but withholds endCursor would have us
        // collecting duplicates until the safety valve trips.
        if (pageInfo.hasNextPage && !pageInfo.endCursor) {
          break;
        }
      }
      return events;
    },
    [client, courseCode],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      fetchAllEvents(COURSE_BOOKABLE_EVENTS),
      fetchAllEvents(COURSE_NON_BOOKABLE_EVENTS),
    ])
      .then(([bookable, nonBookable]) => {
        if (cancelled) return;
        setBookableEvents(bookable);
        setNonBookableEvents(nonBookable);
        setLoading(false);
      })
      .catch((fetchError) => {
        if (cancelled) return;
        setError(fetchError);
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fetchAllEvents]);

  return { loading, error, bookableEvents, nonBookableEvents };
};
